import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";

// Redux
import { useSelector } from "react-redux";

// API
import ItemService from "../../../../services/supplier_appCommonService";

// Components
import Layout from "../../../../Layout/Layout";
import { InputField } from "../../../../Components/formElements/InputField";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";
import Constants from "../../../../common/SupplierConstants";
import { formatNumber } from "../../../../common/Validations";
import FileViewerModal from "../../../../Components/FileViewer/FileViewerModal";
import toast from "react-hot-toast";

const NonPoInvoiceDetails = () => {
  localStorage.setItem("menu", "Purchasing");
  localStorage.setItem("submenu", "Non PO Invoice");

  // Read invoice number from Redux store (same pattern as InvoiceDetails.js)
  let invoiceNumber = useSelector(
    (state) => state?.purchasing?.purchase_order?.invoiceNumber,
  ); // invoiceNumber from Store

  // Read invoice number from Redux store

  // Data state
  const [formInputs, setFormInputs] = useState({
    transactionActivity: "",
    subTransactionActivity: "",
    vendorCode: "",
    purchasingDocumentNr: "",
    purchasingDocumentDate: "",
    purchasingDocumentValue: "",
    currency: "",
    currencyCode: "",
    paymentTerms: "",
    totalInvoiceAmount: "",
    sumOfCurrentAmt: "",
    notes: "",
    weighSlipQty: "",
    itemClass: "",
    vendorInvoiceNo: "",
    vendorInvoiceDate: "",
    vendorInvoiceCurrency: "",
    vendorBasicValue: "",
    vendorFreightValue: "",
    vendorTaxValue: "",
    vendorOtherChargesValue: "",
    vendorTotalInvoiceValue: "",
    invoicedValue: "",
    generatedBy: "",
    createdBy: "",
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Accordion state
  const [isCardVisible, setCardVisible] = useState(true);
  const [isCardVisible2, setCardVisible2] = useState(true);

  // Read-only: disable all edits
  const getDisabledStatus = true;

  // View attachment files
  const [getShowFileViewModal, setShowFileViewModal] = useState(false);
  const [getFileViewData, setFileViewData] = useState(null);

  const toggleCardVisibility = () => {
    setCardVisible(!isCardVisible);
  };
  const toggleCardVisibility2 = () => {
    setCardVisible2(!isCardVisible2);
  };

  const handleViewFile = (fileAttachmentId) => {
    setFileViewData(fileAttachmentId);
    setShowFileViewModal(true);
  };
  const handleViewFileHide = () => {
    setShowFileViewModal(false);
  };

  const onInputChange = ({ target: { name, value } }) => {
    setFormInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Load initial page data (same approach as InvoiceDetails.js)
  const loadInitialPageData = async () => {
    setLoading(true);
    if (invoiceNumber) {
      let obj = {
        documentNumber: invoiceNumber,
      };
      const toastId = toast.loading("Loading...", {
        position: "top-right",
      });

      await ItemService.getInvoiceDetail(obj).then((item) => {
        if (item?.status === 200) {
          let itemData = item?.data[0]?.items;
          const singleItem = item?.data?.[0];
          let cleanedItem = {};

          if (singleItem && typeof singleItem === "object") {
            cleanedItem = { ...singleItem };
            if (cleanedItem.hasOwnProperty("")) {
              delete cleanedItem[""];
            }

            // Invoice Bill Document / attachment mapping
            if (!cleanedItem.invoiceBillDocument && cleanedItem.attachmentId) {
              cleanedItem.invoiceBillDocument = cleanedItem.attachmentId;
            }

            // Supporting Document fallback
            if (!cleanedItem.supportingDocument && cleanedItem.attachmentId) {
              cleanedItem.supportingDocument = cleanedItem.attachmentId;
            }

            // PO Value fallback
            if (!cleanedItem.purchasingDocumentValue) {
              cleanedItem.purchasingDocumentValue =
                cleanedItem.vendorTotalInvoiceValue ||
                cleanedItem.purchasingDocumentValue ||
                0;
            }

            // Weigh Slip Qty = sum of invoice quantities
            if (
              !cleanedItem.totalOrderQuantity &&
              Array.isArray(singleItem.items) &&
              singleItem.items.length
            ) {
              cleanedItem.totalOrderQuantity = singleItem.items.reduce(
                (sum, it) => sum + (Number(it.invoiceQuantity) || 0),
                0,
              );
            }

            // Map item-level rows for display
            const itemRows = Array.isArray(singleItem.items)
              ? singleItem.items
              : [];
            cleanedItem.items = itemRows.map((it, idx) => {
              const qty =
                Number(it.invoiceQuantity) || Number(it.quantity) || 0;
              const unitPrice =
                Number(it.unitPrice) ||
                Number(it.orderPriceUnit) ||
                Number(it.originalUnitPrice) ||
                0;
              const itemValue = unitPrice * qty || Number(it.totalPrice) || 0;
              return {
                ...it,
                local_id: it.local_id ?? idx + 1,
                orderUnitOfMeasure:
                  it.orderUnitOfMeasure || it.uOM || it.unit || "",
                originalUnitPrice: it.originalUnitPrice ?? unitPrice,
                orderPriceUnit: it.orderPriceUnit ?? unitPrice,
                invoiceQuantity: it.invoiceQuantity ?? it.quantity ?? 0,
                totalPrice: it.totalPrice ?? itemValue,
                netValue: it.netValue ?? itemValue,
                taxAmount: it.taxAmount ?? 0,
                grnQuantity: it.grnQuantity ?? it.invoiceQuantity ?? 0,
                invBalancePurchaseQuantity: it.invBalancePurchaseQuantity ?? 0,
                currencyCode:
                  it.currencyCode || cleanedItem.currencyCode || "INR",
              };
            });
            itemData = cleanedItem.items;
          }

          setFormInputs((prev) => ({ ...prev, ...cleanedItem }));
          setData(itemData);
        } else {
          toast.error(item?.message || "Please try again !!", {
            duration: 4000,
            position: "top-right",
          });
        }
        toast.dismiss(toastId);
      });
      setLoading(false);
    } else {
      window.history.back();
    }
  };

  useEffect(() => {
    loadInitialPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber]);

  // Invoice item table columns (read-only view, matching InvoiceDetails.js)
  const invoiceDataColumns = [
    {
      name: "Invoice Item Nr",
      selector: (row) => row.invoiceItemNr,
      sortable: true,
      width: "150px",
    },
    {
      name: "Material/Service",
      selector: (row) => row.materialCode,
      cell: (row) => (
        <div>
          <p className="m-0 p-0 pt-1 fw-bold">{row.materialCode}</p>
          <p className="m-0 p-0">{row.materialDescription}</p>
        </div>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: "Invoice Qty",
      cell: (row) => <>{row.invoiceQuantity}</>,
      sortable: true,
      width: "150px",
    },
    {
      name: "Oder Unit",
      selector: (row) => row.orderUnitOfMeasure || "--",
      sortable: true,
      width: "120px",
    },
    {
      name: "Unit Price",
      selector: (row) => row.orderPriceUnit || "--",
      cell: (row) => (
        <InputField
          className="inputBox mt-1"
          name="orderPriceUnit"
          placeholder=""
          value={row.orderPriceUnit}
          disabled={getDisabledStatus}
        />
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Item Value",
      selector: (row) => row.totalPrice,
      cell: (row) => <>{formatNumber(row.totalPrice, "comma")}</>,
      sortable: true,
      width: "140px",
    },
    {
      name: "Tax Code",
      selector: (row) => row.taxCode,
      sortable: true,
      width: "130px",
    },
    {
      name: "Tax Amount",
      selector: (row) => row.taxAmount,
      cell: (row) => <>{formatNumber(row.taxAmount, "comma")}</>,
      sortable: true,
      width: "140px",
    },
    {
      name: "Total Value",
      selector: (row) => row.netValue,
      cell: (row) => <>{formatNumber(row.netValue, "comma")}</>,
      sortable: true,
      width: "180px",
    },
    {
      name: "Currency",
      selector: (row) => row.currencyCode || "INR",
      sortable: true,
      width: "120px",
    },
  ];

  // Read-only input date formatter - supports "DD/Mon/YYYY" and "YYYY-MM-DD"
  const toInputDate = (dateStr) => {
    if (!dateStr) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr;

    // Handle "DD/Mon/YYYY" format (e.g., "31/Aug/2020")
    const slashParts = dateStr.split("/");
    if (slashParts.length === 3) {
      const [day, month, year] = slashParts;
      const monthAbbrs = {
        Jan: "01",
        Feb: "02",
        Mar: "03",
        Apr: "04",
        May: "05",
        Jun: "06",
        Jul: "07",
        Aug: "08",
        Sep: "09",
        Oct: "10",
        Nov: "11",
        Dec: "12",
      };
      const monthNum =
        monthAbbrs[month] || String(Number(month)).padStart(2, "0");
      return `${year}-${monthNum}-${String(Number(day)).padStart(2, "0")}`;
    }

    // Handle "DD-MM-YYYY" format
    const dashParts = dateStr.split("-");
    if (dashParts.length === 3) {
      const [day, month, year] = dashParts;
      return `${year}-${String(Number(month)).padStart(2, "0")}-${String(Number(day)).padStart(2, "0")}`;
    }

    return dateStr;
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} lg={12} className="rightSide quoation">
          <div className="rightSideInner PageCommonCSS">
            <h2 className="mainTitle">
              Non-PO Invoice Details{" "}
              <span>
                {formInputs?.invoiceStatusHeader === "New" ||
                formInputs?.invoiceStatusHeader === "Invoice Created" ? (
                  <span className="bg-table-status bg-table-new-status headerStatusDetailedPage">
                    {formInputs?.invoiceStatusHeader || "New"}
                  </span>
                ) : formInputs?.invoiceStatusHeader ===
                  "Pending for Approval" ? (
                  <span className="bg-table-status bg-table-progress-status headerStatusDetailedPage">
                    {formInputs?.invoiceStatusHeader}
                  </span>
                ) : formInputs?.invoiceStatusHeader === "Approved" ||
                  formInputs?.invoiceStatusHeader === "Invoice Approved" ? (
                  <span className="bg-table-status bg-table-success-status headerStatusDetailedPage">
                    {formInputs?.invoiceStatusHeader}
                  </span>
                ) : (
                  formInputs?.invoiceStatusHeader || "--"
                )}
              </span>
            </h2>
            <p className="subText">Invoice Details</p>
            <hr />

            <Row className="mb-4">
              <Col xs={12} md={4} lg={4} className="text-left">
                <button
                  className="btnBack"
                  onClick={() => window.history.back()}
                >
                  <i className="bi bi-arrow-return-left"></i> Go Back
                </button>
              </Col>
            </Row>

            <>
              {/* Invoice header - all fields read-only */}
              <div className="accordion mb-4" id="accordionExample">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${
                            isCardVisible
                              ? "bi-chevron-right"
                              : "bi-chevron-down"
                          }`}
                          onClick={toggleCardVisibility}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">IH</span>
                      </Col>
                      <Col xs={12} md={9} lg={9}>
                        <span className="headerTitle">Invoice Header</span>
                        <span className="subHeaderTitle">Invoice Header</span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className={`accordion-collapse collapse show ${
                      isCardVisible ? "visible" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <Row className="pt-2 pb-4">
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Invoice Date"
                            name="vendorInvoiceDate"
                            placeholder=""
                            type="date"
                            value={toInputDate(formInputs?.vendorInvoiceDate)}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>

                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <InputField
                            className="inputBox"
                            label="Invoice Value"
                            name="vendorTotalInvoiceValue"
                            placeholder=""
                            value={formInputs?.vendorTotalInvoiceValue || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>

                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Currency"
                            name="currency"
                            placeholder=""
                            value={
                              formInputs?.currencyCode && formInputs?.currency
                                ? `${formInputs.currencyCode} - ${formInputs.currency}`
                                : formInputs?.currency || ""
                            }
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>

                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding"
                        >
                          <p className="fw-medium m-0">Invoice</p>
                          {(formInputs?.invoiceBillDocument && (
                            <img
                              src="assets/img/newIcons/attached-file.png"
                              className="mt-1 attachmentIcon"
                              alt="attachments"
                              style={{ height: "24px", width: "24px" }}
                              onClick={() =>
                                handleViewFile(formInputs?.invoiceBillDocument)
                              }
                            />
                          )) ||
                            "--"}
                        </Col>

                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Vendor"
                            name="vendorCode"
                            placeholder=""
                            value={formInputs?.vendorCode || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>

                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Vendor Name"
                            name="vendorName"
                            placeholder=""
                            value={formInputs?.vendorName || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>

                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <TextAreaField
                            className="inputBox"
                            label="Notes"
                            name="notes"
                            placeholder=""
                            value={formInputs?.notes || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Invoice Number"
                            name="invoiceNumber"
                            placeholder=""
                            value={formInputs?.invoiceNumber || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Vendor Invoice No"
                            name="vendorInvoiceNo"
                            placeholder=""
                            value={formInputs?.vendorInvoiceNo || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Vendor Invoice Date"
                            name="invoiceDate"
                            placeholder=""
                            value={toInputDate(formInputs?.invoiceDate)}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Invoice Type"
                            name="invoiceType"
                            placeholder=""
                            value={formInputs?.invoiceType || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Payment Terms"
                            name="paymentTerms"
                            placeholder=""
                            value={formInputs?.paymentTerms || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Currency Code"
                            name="vendorInvoiceCurrency"
                            placeholder=""
                            value={formInputs?.vendorInvoiceCurrency || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Vendor Basic Value"
                            name="vendorBasicValue"
                            placeholder=""
                            value={formInputs?.vendorBasicValue || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Vendor Freight Value"
                            name="vendorFreightValue"
                            placeholder=""
                            value={formInputs?.vendorFreightValue || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Vendor Tax Value"
                            name="vendorTaxValue"
                            placeholder=""
                            value={formInputs?.vendorTaxValue || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Vendor Other Charges"
                            name="vendorOtherChargesValue"
                            placeholder=""
                            value={formInputs?.vendorOtherChargesValue || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Vendor Total Invoice Value"
                            name="vendorTotalInvoiceValue"
                            placeholder=""
                            value={formInputs?.vendorTotalInvoiceValue || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                        <Col
                          xs={12}
                          md={4}
                          lg={4}
                          className="commTopButtonRightLeftPadding d-flex-column"
                        >
                          <InputField
                            className="inputBox"
                            label="Invoiced Value"
                            name="invoicedValue"
                            placeholder=""
                            value={formInputs?.invoicedValue || ""}
                            onChange={onInputChange}
                            disabled={getDisabledStatus}
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Service Item Details */}
              <div className="accordion mb-4" id="accordionExample">
                <div className="accordion-item">
                  <h2 className="accordion-header">
                    <Row className="accordion-button">
                      <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                        <i
                          className={`bi ${
                            isCardVisible2
                              ? "bi-chevron-right"
                              : "bi-chevron-down"
                          }`}
                          onClick={toggleCardVisibility2}
                        ></i>
                      </Col>
                      <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                        <span className="iconLogo">ID</span>
                      </Col>
                      <Col xs={12} md={8} lg={8}>
                        <span className="headerTitle">
                          Invoice Service Item Details
                        </span>
                        <span className="subHeaderTitle">
                          Invoice Service Item Details
                        </span>
                      </Col>
                    </Row>
                  </h2>
                  <div
                    className={`accordion-collapse collapse show ${
                      isCardVisible2 ? "visible" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      <Row>
                        <Col xs={12} md={12} lg={12}>
                          <DataTable
                            columns={invoiceDataColumns}
                            data={data}
                            progressPending={loading}
                            paginationRowsPerPageOptions={
                              Constants.ROW_PER_PAGE
                            }
                            pagination
                          />
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>
            </>
          </div>
        </Col>
      </Row>

      <FileViewerModal
        show={getShowFileViewModal}
        onHide={handleViewFileHide}
        fileid={getFileViewData}
      />
    </main>
  );
};

export default NonPoInvoiceDetails;
